import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ImagePlus, Send, ArrowLeft, X, Film } from "lucide-react";

const POLL_CONVS_MS = 5000;
const POLL_MSGS_MS = 3000;
const VIDEO_MAX_SECONDS = 180; // 3 minutes
const VIDEO_MAX_BYTES = 100 * 1024 * 1024; // 100 MB safeguard

const fmtTimestamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  const diffDays = Math.floor((today - d) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return d.toLocaleDateString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const MESSAGE_LIMIT = 2000;

const Messages = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [activeConv, setActiveConv] = useState(null);
  const [sending, setSending] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { url, file }
  const [pendingVideo, setPendingVideo] = useState(null); // { url, duration }
  const [uploading, setUploading] = useState(false);
  const [cloudinaryReady, setCloudinaryReady] = useState(false);
  const messagesEndRef = useRef(null);
  const lastMsgTimeRef = useRef(null);
  const showMobilePane = !!id; // on mobile: show list or thread, never both

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
      if (!id && data.length > 0) {
        navigate(`/messages/${data[0].id}`, { replace: true });
      }
    } catch {
      // silent — polling
    }
  }, [id, navigate]);

  const loadMessages = useCallback(async (initial = false) => {
    if (!id) return;
    try {
      if (initial) {
        const { data } = await api.get(`/conversations/${id}/messages`);
        setMessages(data);
        if (data.length) lastMsgTimeRef.current = data[data.length - 1].created_at;
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "auto" }), 50);
        // Mark conversation read on open
        api.post(`/conversations/${id}/read`).catch(() => {});
      } else if (lastMsgTimeRef.current) {
        const { data } = await api.get(`/conversations/${id}/messages`, {
          params: { since: lastMsgTimeRef.current },
        });
        if (data.length) {
          setMessages((prev) => [...prev, ...data]);
          lastMsgTimeRef.current = data[data.length - 1].created_at;
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
          // Mark read whenever new messages from the other arrive while we're viewing
          if (data.some((m) => m.sender_id !== user?.id)) {
            api.post(`/conversations/${id}/read`).catch(() => {});
          }
        }
      }
    } catch {
      // silent — polling
    }
  }, [id, user?.id]);

  // Find / set active conversation
  useEffect(() => {
    if (id && conversations.length) {
      const c = conversations.find((c) => c.id === id);
      if (c) setActiveConv(c);
    }
  }, [id, conversations]);

  // Initial load + polling
  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => {
    const t = setInterval(loadConvs, POLL_CONVS_MS);
    return () => clearInterval(t);
  }, [loadConvs]);

  useEffect(() => {
    setMessages([]);
    lastMsgTimeRef.current = null;
    if (id) loadMessages(true);
  }, [id, loadMessages]);

  useEffect(() => {
    if (!id) return;
    const t = setInterval(() => loadMessages(false), POLL_MSGS_MS);
    return () => clearInterval(t);
  }, [id, loadMessages]);

  useEffect(() => {
    api.get("/cloudinary/status").then(({ data }) => setCloudinaryReady(data?.configured)).catch(() => {});
  }, []);

  const handlePickImage = async (file) => {
    if (!file) return;
    if (!cloudinaryReady) {
      toast.error("Image attachments unavailable.");
      return;
    }
    setUploading(true);
    try {
      const sig = await api.get("/cloudinary/signature", {
        params: { resource_type: "image", folder: "messages/" },
      });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.data.api_key);
      fd.append("timestamp", sig.data.timestamp);
      fd.append("signature", sig.data.signature);
      fd.append("folder", sig.data.folder);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.data.cloud_name}/image/upload`,
        { method: "POST", body: fd },
      );
      const u = await res.json();
      if (u.secure_url) {
        setPendingImage({ url: u.secure_url });
      } else {
        toast.error("Upload failed.");
      }
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const probeVideoDuration = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        const d = v.duration;
        URL.revokeObjectURL(url);
        resolve(d);
      };
      v.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Could not read video."));
      };
      v.src = url;
    });

  const handlePickVideo = async (file) => {
    if (!file) return;
    if (!cloudinaryReady) {
      toast.error("Video attachments unavailable.");
      return;
    }
    if (file.size > VIDEO_MAX_BYTES) {
      toast.error("This video is too large. Please choose one under 100 MB.");
      return;
    }
    // Duration check first — friendlier than uploading + rejecting server-side.
    let duration;
    try {
      duration = await probeVideoDuration(file);
    } catch {
      toast.error("Could not read that video file.");
      return;
    }
    if (duration && duration > VIDEO_MAX_SECONDS) {
      toast.error(`Videos in Private Notes are capped at 3 minutes (yours is ${Math.round(duration)}s).`);
      return;
    }
    setUploading(true);
    try {
      const sig = await api.get("/cloudinary/signature", {
        params: { resource_type: "video", folder: "messages-video/" },
      });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.data.api_key);
      fd.append("timestamp", sig.data.timestamp);
      fd.append("signature", sig.data.signature);
      fd.append("folder", sig.data.folder);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.data.cloud_name}/video/upload`,
        { method: "POST", body: fd },
      );
      const u = await res.json();
      if (u.secure_url) {
        setPendingVideo({ url: u.secure_url, duration });
      } else {
        toast.error("Upload failed.");
      }
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!id || sending) return;
    const trimmed = text.trim();
    if (!trimmed && !pendingImage && !pendingVideo) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${id}/messages`, {
        text: trimmed,
        image_url: pendingImage?.url || null,
        video_url: pendingVideo?.url || null,
        video_duration_seconds: pendingVideo?.duration || null,
      });
      setMessages((prev) => [...prev, data]);
      lastMsgTimeRef.current = data.created_at;
      setText("");
      setPendingImage(null);
      setPendingVideo(null);
      loadConvs();
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send.");
    } finally {
      setSending(false);
    }
  };

  const renderMessageBubble = (m, idx, all) => {
    const mine = m.sender_id === user?.id;
    const otherId = activeConv?.other_user?.id;
    const otherRead = mine && otherId && Array.isArray(m.read_by) && m.read_by.includes(otherId);
    const isLastMine = mine && (idx === all.length - 1 || all[idx + 1].sender_id !== m.sender_id);
    return (
      <div
        key={m.id}
        data-testid={`message-${m.id}`}
        style={{ display: "flex", flexDirection: "column", alignItems: mine ? "flex-end" : "flex-start" }}
      >
        <div
          style={{
            maxWidth: "75%",
            background: mine ? "var(--brand-accent)" : "#FFFFFF",
            color: mine ? "#FAF9F5" : "var(--text-primary)",
            padding: (m.image_url || m.video_url) ? 6 : "12px 18px",
            borderRadius: 2,
            border: mine ? "none" : "1px solid var(--border-default)",
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 18,
            lineHeight: 1.45,
            boxShadow: "0 1px 2px rgba(51,51,51,0.04)",
          }}
        >
          {m.image_url && (
            <img
              src={m.image_url}
              alt=""
              style={{
                display: "block",
                width: "100%",
                maxWidth: 320,
                maxHeight: 420,
                objectFit: "cover",
                marginBottom: m.text ? 8 : 0,
                borderRadius: 1,
              }}
            />
          )}
          {m.video_url && (
            <video
              data-testid={`message-video-${m.id}`}
              src={m.video_url}
              controls
              playsInline
              preload="metadata"
              style={{
                display: "block",
                width: "100%",
                maxWidth: 320,
                maxHeight: 420,
                marginBottom: m.text ? 8 : 0,
                borderRadius: 1,
                background: "#000",
              }}
            />
          )}
          {m.text && (
            <div style={{ padding: (m.image_url || m.video_url) ? "6px 12px 8px 12px" : 0 }}>{m.text}</div>
          )}
        </div>
        <div
          style={{
            fontSize: 10,
            color: "var(--text-muted)",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            marginTop: 4,
            fontFamily: "'Outfit', sans-serif",
          }}
        >
          {fmtTimestamp(m.created_at)}
          {isLastMine && otherRead && <span style={{ marginLeft: 10 }}>· Read</span>}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="messages-page" className="app-shell">
      <Navbar />
      <LiveNowBanner />
      <div style={{ paddingTop: 90 }}>
        <div className="container-belle" style={{ paddingTop: 24, paddingBottom: 24 }}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>Private notes</div>
          <h1 className="editorial-h1" style={{ fontSize: "clamp(32px, 4.5vw, 48px)", marginTop: 8 }}>
            Private Notes
          </h1>
        </div>

        <div
          className="container-belle messages-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "340px 1fr",
            border: "1px solid var(--border-default)",
            minHeight: "70vh",
            background: "var(--surface)",
          }}
        >
          {/* Conversation list */}
          <div
            data-testid="conversation-list"
            className="messages-list"
            style={{
              borderRight: "1px solid var(--border-default)",
              overflowY: "auto",
              display: showMobilePane ? undefined : "block",
            }}
          >
            {conversations.length === 0 ? (
              <div style={{ padding: 32 }}>
                <p
                  style={{
                    color: "var(--text-muted)",
                    fontStyle: "italic",
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 19,
                    lineHeight: 1.4,
                  }}
                >
                  No letters yet. Visit a member&apos;s profile to begin a correspondence.
                </p>
              </div>
            ) : (
              conversations.map((c) => {
                const isActive = c.id === id;
                const unread = c.unread_count || 0;
                return (
                  <Link
                    key={c.id}
                    to={`/messages/${c.id}`}
                    data-testid={`conversation-${c.id}`}
                    style={{
                      display: "flex",
                      gap: 14,
                      padding: "18px 22px",
                      borderBottom: "1px solid var(--border-default)",
                      background: isActive ? "var(--bg-alt)" : "transparent",
                      alignItems: "center",
                      textDecoration: "none",
                      color: "var(--text-primary)",
                    }}
                  >
                    {c.other_user?.avatar_url ? (
                      <img
                        src={c.other_user.avatar_url}
                        alt=""
                        style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                      />
                    ) : (
                      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--border-default)", flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                        <div className="font-serif" style={{ fontSize: 18, fontWeight: unread > 0 ? 600 : 500 }}>
                          {c.other_user?.display_name}
                        </div>
                        <div className="overline" style={{ fontSize: 10, color: "var(--text-muted)" }}>
                          {c.last_message?.created_at ? fmtTimestamp(c.last_message.created_at) : ""}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: unread > 0 ? "var(--text-primary)" : "var(--text-secondary)",
                          fontWeight: unread > 0 ? 500 : 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          marginTop: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                          {c.last_message?.image_url && !c.last_message?.text
                            ? "📷 Photograph"
                            : c.last_message?.text || "—"}
                        </span>
                        {unread > 0 && (
                          <span
                            data-testid={`conv-unread-${c.id}`}
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: "var(--brand-accent)",
                              flexShrink: 0,
                              marginLeft: "auto",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {/* Conversation pane */}
          <div
            className="messages-pane"
            style={{ display: "flex", flexDirection: "column", minHeight: "70vh" }}
          >
            {activeConv ? (
              <>
                <div
                  style={{
                    padding: "18px 28px",
                    borderBottom: "1px solid var(--border-default)",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    background: "var(--surface)",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => navigate("/messages")}
                    aria-label="Back to letters"
                    className="messages-back"
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      color: "var(--text-primary)",
                      display: "none",
                    }}
                  >
                    <ArrowLeft size={20} strokeWidth={1.4} />
                  </button>
                  {activeConv.other_user?.avatar_url ? (
                    <img
                      src={activeConv.other_user.avatar_url}
                      alt=""
                      style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover" }}
                    />
                  ) : (
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--border-default)" }} />
                  )}
                  <div>
                    <Link
                      to={`/profile/${activeConv.other_user?.username}`}
                      className="font-serif"
                      style={{ fontSize: 20, fontWeight: 500, color: "var(--text-primary)" }}
                    >
                      {activeConv.other_user?.display_name}
                    </Link>
                    <div className="overline" style={{ marginTop: 2 }}>
                      @{activeConv.other_user?.username}
                    </div>
                  </div>
                </div>

                <div
                  data-testid="message-thread"
                  style={{
                    flex: 1,
                    padding: "32px 28px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 18,
                    background: "var(--bg-alt)",
                  }}
                >
                  {messages.length === 0 && (
                    <p
                      style={{
                        textAlign: "center",
                        color: "var(--text-muted)",
                        fontStyle: "italic",
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 18,
                        marginTop: 40,
                      }}
                    >
                      The page is blank. Write the first line.
                    </p>
                  )}
                  {messages.map((m, idx) => renderMessageBubble(m, idx, messages))}
                  <div ref={messagesEndRef} />
                </div>

                {pendingImage && (
                  <div
                    style={{
                      padding: "10px 20px",
                      borderTop: "1px solid var(--border-default)",
                      background: "var(--surface)",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <img
                      src={pendingImage.url}
                      alt=""
                      style={{ width: 56, height: 56, objectFit: "cover", border: "1px solid var(--border-default)" }}
                    />
                    <div style={{ flex: 1, fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", color: "var(--text-muted)", fontSize: 14 }}>
                      Photograph attached
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingImage(null)}
                      data-testid="remove-pending-image"
                      aria-label="Remove attachment"
                      style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 6 }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                {pendingVideo && (
                  <div
                    style={{
                      padding: "10px 20px",
                      borderTop: "1px solid var(--border-default)",
                      background: "var(--surface)",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <video
                      src={pendingVideo.url}
                      muted
                      style={{ width: 72, height: 56, objectFit: "cover", border: "1px solid var(--border-default)", background: "#000" }}
                    />
                    <div style={{ flex: 1, fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", color: "var(--text-muted)", fontSize: 14 }}>
                      Video attached · {Math.round(pendingVideo.duration || 0)}s
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingVideo(null)}
                      data-testid="remove-pending-video"
                      aria-label="Remove attachment"
                      style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 6 }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                {text.length > 1800 && (
                  <div
                    data-testid="message-counter"
                    style={{
                      padding: "6px 20px 0 20px",
                      background: "var(--surface)",
                      fontSize: 11,
                      color: text.length >= MESSAGE_LIMIT ? "var(--brand-accent)" : "var(--text-muted)",
                      fontFamily: "'Outfit', sans-serif",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      textAlign: "right",
                    }}
                  >
                    {text.length >= MESSAGE_LIMIT
                      ? "Letter is full — send it and continue in another"
                      : `${MESSAGE_LIMIT - text.length} characters remaining`}
                  </div>
                )}

                <form
                  onSubmit={send}
                  style={{
                    padding: 16,
                    borderTop: "1px solid var(--border-default)",
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-end",
                    background: "var(--surface)",
                  }}
                >
                  <label
                    aria-label="Attach photograph"
                    title="Attach photograph"
                    style={{
                      cursor: uploading ? "wait" : "pointer",
                      padding: "10px 12px",
                      color: uploading ? "var(--text-muted)" : "var(--text-primary)",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <ImagePlus size={20} strokeWidth={1.4} />
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      disabled={uploading}
                      onChange={(e) => handlePickImage(e.target.files?.[0])}
                      data-testid="message-image-input"
                    />
                  </label>
                  <label
                    aria-label="Attach video (up to 3 minutes)"
                    title="Attach video · up to 3 min"
                    style={{
                      cursor: uploading ? "wait" : "pointer",
                      padding: "10px 12px",
                      color: uploading ? "var(--text-muted)" : "var(--text-primary)",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <Film size={20} strokeWidth={1.4} />
                    <input
                      type="file"
                      accept="video/*"
                      style={{ display: "none" }}
                      disabled={uploading}
                      onChange={(e) => handlePickVideo(e.target.files?.[0])}
                      data-testid="message-video-input"
                    />
                  </label>
                  <textarea
                    data-testid="message-input"
                    value={text}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v.length > MESSAGE_LIMIT) {
                        setText(v.slice(0, MESSAGE_LIMIT));
                        toast("This letter is full — send it and continue in another.");
                      } else {
                        setText(v);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send(e);
                      }
                    }}
                    placeholder="Write a letter…"
                    rows={1}
                    maxLength={MESSAGE_LIMIT}
                    style={{
                      flex: 1,
                      border: "1px solid var(--border-default)",
                      padding: "10px 14px",
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: 17,
                      lineHeight: 1.4,
                      resize: "none",
                      maxHeight: 120,
                      background: "var(--bg-alt)",
                      borderRadius: 2,
                    }}
                  />
                  <button
                    type="submit"
                    data-testid="message-send-button"
                    disabled={sending || (!text.trim() && !pendingImage && !pendingVideo)}
                    aria-label="Send letter"
                    style={{
                      padding: "10px 18px",
                      background: "var(--brand-accent)",
                      color: "#FAF9F5",
                      border: "none",
                      cursor: sending ? "wait" : "pointer",
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      fontSize: 11,
                      fontFamily: "'Outfit', sans-serif",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: !text.trim() && !pendingImage && !pendingVideo ? 0.5 : 1,
                    }}
                  >
                    <Send size={14} strokeWidth={1.6} />
                    Send
                  </button>
                </form>
              </>
            ) : (
              <div style={{ flex: 1, display: "grid", placeItems: "center", padding: 60 }}>
                <p
                  style={{
                    color: "var(--text-muted)",
                    fontStyle: "italic",
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 22,
                    textAlign: "center",
                    maxWidth: 380,
                    lineHeight: 1.4,
                  }}
                >
                  Choose a correspondent on the left to begin reading.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .messages-grid { grid-template-columns: 1fr !important; }
          .messages-list { display: ${showMobilePane ? "none" : "block"} !important; }
          .messages-pane { display: ${showMobilePane ? "flex" : "none"} !important; }
          .messages-back { display: inline-flex !important; }
        }
      `}</style>
    </div>
  );
};

export default Messages;
